import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AuditEntry } from '@/lib/schemas';
import { AuditDiff } from './audit-diff';

interface AuditTableProps {
  entries: AuditEntry[];
  onUndo: (auditId: string) => void;
}

export function AuditTable({ entries, onUndo }: AuditTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function toggleExpand(id: string) {
    setExpandedId((current) => (current === id ? null : id));
  }

  return (
    <div className="rounded-lg border border-ndd-800 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Time</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Tool</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Risk</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center text-ndd-400">
                No audit entries found
              </TableCell>
            </TableRow>
          )}
          {entries.map((entry) => (
            <Fragment key={entry.audit_id}>
              <TableRow className={entry.rolled_back ? 'opacity-50 line-through' : ''}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => toggleExpand(entry.audit_id)}
                    className="text-ndd-400 hover:text-white"
                  >
                    {expandedId === entry.audit_id ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                </TableCell>
                <TableCell className="text-xs text-ndd-300 whitespace-nowrap">
                  {new Date(entry.timestamp).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm">
                  <span className="capitalize">{entry.actor}</span>
                  <span className="block text-xs text-ndd-500 normal-case">{entry.user_email}</span>
                </TableCell>
                <TableCell className="text-sm font-mono text-xs">{entry.tool}</TableCell>
                <TableCell className="text-xs text-ndd-400">
                  {entry.target_sheet}
                  {entry.target_id && <span className="block text-ndd-600">{entry.target_id}</span>}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      entry.risk_level === 'high'
                        ? 'destructive'
                        : entry.risk_level === 'medium'
                          ? 'outline'
                          : 'secondary'
                    }
                  >
                    {entry.risk_level}
                  </Badge>
                </TableCell>
                <TableCell>
                  {entry.rolled_back ? (
                    <Badge variant="secondary">Rolled back</Badge>
                  ) : (
                    <Badge variant="default">{entry.action}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {!entry.rolled_back && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onUndo(entry.audit_id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Undo
                    </Button>
                  )}
                </TableCell>
              </TableRow>

              {expandedId === entry.audit_id && (
                <TableRow key={`${entry.audit_id}-expanded`}>
                  <TableCell colSpan={8} className="bg-ndd-900/30 px-6 py-4">
                    {entry.context && (
                      <p className="text-sm text-ndd-300 mb-3">
                        <span className="text-ndd-500">Context: </span>
                        {entry.context}
                      </p>
                    )}
                    <AuditDiff before={entry.before_snapshot} after={entry.after_snapshot} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
